/**
 * Reglas puras del formulario de marca. Los regex son los mismos que aplica el
 * backend en `/upsertbrand`, para fallar en el cliente con el mismo criterio y
 * no gastar un request en un error previsible.
 *
 * Contrato: `argenlotapi/docs/frontend/FRONTEND_BRANDS.md`.
 */

export const BRAND_NAME_MAX_LENGTH = 200;

/** El `#` es obligatorio del lado del servidor; el form lo agrega antes de mandar. */
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
/** Misma regla que `/upsertbrand`. */
export const CONTACT_EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const PHONE_CHARS_REGEX = /^[\d\s+\-().]+$/;
const PHONE_DISALLOWED_CHARS = /[^\d\s+\-().]/g;
const PHONE_MIN_DIGITS = 8;
const PHONE_MAX_DIGITS = 15;

/** El logo no viaja en el payload del form: lo maneja el upload a S3. */
const BRAND_TEXT_FIELDS = [
  'brand_name',
  'primary_color',
  'secondary_color',
  'phone',
  'whatsapp',
  'email',
];

export const EMPTY_BRAND_FORM = {
  brand_name: '',
  logo_url: '',
  primary_color: '',
  secondary_color: '',
  phone: '',
  whatsapp: '',
  email: '',
};

/**
 * Agrega el `#` que el servidor exige, para que el usuario pueda tipear
 * `1F4B4A` sin que le rebote.
 *
 * @param {string} value
 * @returns {string}
 */
export function normalizeHexInput(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

/**
 * Hex de seis dígitos para `<input type="color">`, que no acepta la forma corta.
 *
 * @param {string} value
 * @param {string} [fallback]
 * @returns {string}
 */
export function toColorInputValue(value, fallback = '#489291') {
  const normalized = normalizeHexInput(value);
  if (!HEX_COLOR_REGEX.test(normalized)) return fallback;
  if (normalized.length === 4) {
    const [, r, g, b] = normalized;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return normalized;
}

/**
 * Fila de `brands` → estado del form (sin nulls, para inputs controlados).
 *
 * @param {object|null|undefined} brand
 * @returns {object}
 */
export function brandToForm(brand) {
  if (!brand || typeof brand !== 'object') return { ...EMPTY_BRAND_FORM };
  return {
    brand_name: brand.brand_name || '',
    logo_url: brand.logo_url || '',
    primary_color: brand.primary_color || '',
    secondary_color: brand.secondary_color || '',
    phone: brand.phone || '',
    whatsapp: brand.whatsapp || '',
    email: brand.email || '',
  };
}

/**
 * Teléfono y WhatsApp no aceptan letras: se descartan al tipear o pegar, y
 * no se dejan más de 15 dígitos (el tope del servidor).
 *
 * @param {string} value
 * @returns {string}
 */
export function sanitizePhoneInput(value) {
  const filtered = String(value || '').replace(PHONE_DISALLOWED_CHARS, '');
  const digits = filtered.replace(/\D/g, '');
  if (digits.length <= PHONE_MAX_DIGITS) return filtered;

  let seen = 0;
  return [...filtered].filter((ch) => {
    if (!/\d/.test(ch)) return true;
    if (seen >= PHONE_MAX_DIGITS) return false;
    seen += 1;
    return true;
  }).join('');
}

function validatePhoneField(value, label) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';

  if (!PHONE_CHARS_REGEX.test(trimmed)) {
    return `Ingresá un ${label} válido (números, espacios, +, - o paréntesis).`;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < PHONE_MIN_DIGITS) {
    return `El ${label} debe tener al menos ${PHONE_MIN_DIGITS} dígitos.`;
  }
  if (digits.length > PHONE_MAX_DIGITS) {
    return `El ${label} no puede superar ${PHONE_MAX_DIGITS} dígitos.`;
  }

  return '';
}

/**
 * Errores por campo. El servidor recorta espacios antes de validar, así que un
 * campo que queda vacío después de recortar cuenta como vacío.
 *
 * @param {object} form
 * @returns {Record<string, string>}
 */
export function validateBrandForm(form = {}) {
  const errors = {};

  const brandName = String(form.brand_name || '').trim();
  if (!brandName) {
    errors.brand_name = 'El nombre de la identidad es obligatorio.';
  } else if (brandName.length > BRAND_NAME_MAX_LENGTH) {
    errors.brand_name = `Máximo ${BRAND_NAME_MAX_LENGTH} caracteres.`;
  }

  ['primary_color', 'secondary_color'].forEach((field) => {
    const raw = String(form[field] || '').trim();
    if (raw && !HEX_COLOR_REGEX.test(normalizeHexInput(raw))) {
      errors[field] = 'Usá un color hex válido (#RGB o #RRGGBB).';
    }
  });

  const emailError = validateContactEmail(form.email);
  if (emailError) errors.email = emailError;

  const phoneError = validatePhoneField(form.phone, 'teléfono');
  if (phoneError) errors.phone = phoneError;

  const whatsappError = validatePhoneField(form.whatsapp, 'WhatsApp');
  if (whatsappError) errors.whatsapp = whatsappError;

  return errors;
}

/**
 * Email de la identidad. Vacío es válido (el campo es opcional; `""` lo limpia).
 *
 * @param {string} value
 * @returns {string} Error o string vacío.
 */
export function validateContactEmail(value) {
  const email = String(value || '').trim();
  if (!email) return '';
  if (!CONTACT_EMAIL_REGEX.test(email)) return 'Ingresá un email válido.';
  return '';
}

export function hasBrandFormErrors(errors) {
  return Object.values(errors || {}).some(Boolean);
}

/**
 * Body de `/upsertbrand`. Manda los seis campos de texto siempre: el form es
 * completo, así que vaciar uno significa borrarlo (el servidor lee `""` como
 * limpiar). `logo_url` queda afuera porque lo escribe el upload.
 *
 * `is_default` solo viaja cuando se marca: la principal no se desmarca, se
 * reemplaza marcando otra. No mandar `company_id`: el servidor lo ignora.
 *
 * @param {object} form
 * @param {{ brandId?: string, makeDefault?: boolean }} [options]
 * @returns {object}
 */
export function buildBrandPayload(form = {}, { brandId, makeDefault } = {}) {
  const payload = {};

  const id = String(brandId || '').trim();
  if (id) payload.brand_id = id;

  BRAND_TEXT_FIELDS.forEach((field) => {
    const value = String(form[field] || '').trim();
    if (field === 'primary_color' || field === 'secondary_color') {
      payload[field] = value ? normalizeHexInput(value) : '';
      return;
    }
    payload[field] = value;
  });

  if (makeDefault === true) payload.is_default = true;

  return payload;
}

/**
 * Inicial para el avatar cuando la marca no tiene logo.
 *
 * @param {string} brandName
 * @returns {string}
 */
export function getBrandInitial(brandName) {
  const trimmed = String(brandName || '').trim();
  return (trimmed ? trimmed.charAt(0) : 'M').toUpperCase();
}

/**
 * La marca que usa un share `personalized` sin `brand_id`: la principal del
 * usuario. `brand_id: null` significa "seguí a la principal", no "sin marca".
 *
 * @param {object[]} brands
 * @returns {object|null}
 */
export function findDefaultBrand(brands) {
  const list = Array.isArray(brands) ? brands : [];
  return list.find((brand) => brand?.is_default) || list[0] || null;
}
